import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from "@mui/material";
import Person from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import {ErrorMessage} from "formik";
import * as React from "react";
import {Fragment, useRef} from "react";
import {HelpOutline} from "@mui/icons-material";
import RequiredAsterisk from "../../components/RequiredAsterisk";
import AddressAutocompleteInput from "../../components/AddressAutocompleteInput";
import {useAddressAutofill} from "../../hooks/useAddressAutofill";


export function OwnerAddress({values, handleChange, handleBlur, errors, touched, data} ) {

  let ownerFirstname = useRef();
  let ownerLastname = useRef();
  let ownerStreet = useRef();
  let ownerSuburbTown = useRef();
  let ownerPostcode = useRef();

  const fireChange = (name, value) => handleChange({ target: { name, value } });

  const { suburbs, postcodes, handleSuburbValueChange, handlePostcodeValueChange } = useAddressAutofill({
    state: values.owner_state,
    setState: (value) => fireChange('owner_state', value),
    setSuburb: (value) => fireChange('owner_suburb_town', value),
    setPostcode: (value) => fireChange('owner_postcode', value),
  });

  // copy delivery address over to property owner function
  function handleCopyFromDelivery(e) {

    ownerFirstname.current.value = values.owner_firstname = values.delivery_firstname;
    ownerLastname.current.value = values.owner_lastname = values.delivery_lastname;
    ownerStreet.current.value = values.owner_street = values.delivery_number_street;
    ownerSuburbTown.current.value = values.owner_suburb_town = values.delivery_suburb;
    values.owner_state = values.delivery_state;
    ownerPostcode.current.value = values.owner_postcode = values.delivery_postcode;
    document.getElementById('place-order').click();

  }


  return (

    <Fragment>
          <h5 className="text-secondary mb-3">
            Property Owner Address  <Tooltip title="What is the address of the property owner." arrow><HelpOutline /></Tooltip>
          </h5>

        {
          data.shipping === '0' ?
          <Button className="mb-3" variant="contained" onClick={handleCopyFromDelivery}>Copy from Delivery Address</Button>
          : null
        }


          <FormControl fullWidth className="mb-4">
            <TextField name="owner_firstname"
                       label={<>Owner Firstname <RequiredAsterisk /></>}
                       inputRef={ownerFirstname}
                       value={values.owner_firstname}
                       onChange={handleChange}
                       onBlur={handleBlur}
                       error={!!(touched?.owner_firstname && errors.owner_firstname)}
                       helperText={touched?.owner_firstname && errors.owner_firstname}
                       InputProps={{
                         startAdornment: (
                           <InputAdornment position='start'><Person/></InputAdornment>
                         )
                       }}
            />
          </FormControl>

          <FormControl fullWidth className="mb-4">
            <TextField name="owner_lastname" label={<>Owner Lastname <RequiredAsterisk /></>} variant="outlined"
                       onChange={handleChange}
                       onBlur={handleBlur}
                       inputRef={ownerLastname}
                       value={values.owner_lastname}
                       error={!!(touched?.owner_lastname && errors.owner_lastname)}
                       helperText={touched?.owner_lastname && errors.owner_lastname}
                       InputProps={{
                         startAdornment: (
                           <InputAdornment position='start'><Person/></InputAdornment>
                         )
                       }}
            />
          </FormControl>

          <FormControl fullWidth className="mb-4">
            <TextField name="owner_phone" label={<>Owner Phone <RequiredAsterisk /></>}
                       onChange={handleChange}
                       onBlur={handleBlur}
                       value={values.owner_phone}
                       error={!!(touched?.owner_phone && errors.owner_phone)}
                       helperText={touched?.owner_phone && errors.owner_phone}
                       InputProps={{
                         startAdornment: (<InputAdornment position='start'><PhoneIcon/></InputAdornment>)
                       }}
            />
          </FormControl>

          <FormControl fullWidth className="mb-4">
            <TextField name="owner_email" label={<>Owner Email Address <RequiredAsterisk /></>}
                       onChange={handleChange}
                       onBlur={handleBlur}
                       value={values.owner_email}
                       error={!!(touched?.owner_email && errors.owner_email)}
                       helperText={touched?.owner_email && errors.owner_email}
                       InputProps={{
                         startAdornment: (
                           <InputAdornment position='start'><EmailIcon/></InputAdornment>
                         )
                       }}
            />
          </FormControl>

          <FormControl fullWidth className="mb-4">
            <TextField name="owner_street" label={<>Owner Street Number & Name <RequiredAsterisk /></>}
                       inputRef={ownerStreet}
                       onChange={handleChange}
                       onBlur={handleBlur}
                       value={values.owner_street}
                       error={!!(touched?.owner_street && errors.owner_street)}
                       helperText={touched?.owner_street && errors.owner_street}
                       InputProps={{
                         startAdornment: (
                           <InputAdornment position='start'><HomeIcon/></InputAdornment>
                         )
                       }}
            />
          </FormControl>

          <FormControl fullWidth className="mb-4">
            <InputLabel id="owner-state-label">State <RequiredAsterisk /></InputLabel>
            <Select name="owner_state"
                    labelId="owner-state-label"
                    value={values.owner_state}
                    id="owner-state"
                    label="State *"
                    variant="outlined"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(touched?.owner_state && errors.owner_state)}
            >
              <MenuItem selected value=""><em>None</em></MenuItem>
              <MenuItem value="ACT">Australian Capital Territory</MenuItem>
              <MenuItem value="NSW">New South Wales</MenuItem>
              <MenuItem value="NT">Northern Territory</MenuItem>
              <MenuItem value="QLD">Queensland</MenuItem>
              <MenuItem value="SA">South Australia</MenuItem>
              <MenuItem value="TAS">Tasmania</MenuItem>
              <MenuItem value="VIC">Victoria</MenuItem>
              <MenuItem value="WA">Western Australia</MenuItem>
            </Select>
            <ErrorMessage style={{letterSpacing: '0.03333em', fontSize: '0.75rem', marginLeft: '14px'}}
                          component="div" name="owner_state" className="help-block errors text-danger"/>
          </FormControl>

          <FormControl fullWidth className="mb-4">
            <div className={`mui-like-field${touched?.owner_suburb_town && errors.owner_suburb_town ? ' mui-like-field-error' : ''}`}>
              <span className="mui-like-label">Owner Suburb or Town <RequiredAsterisk /></span>
              <AddressAutocompleteInput
                name="owner_suburb_town"
                inputRef={ownerSuburbTown}
                value={values.owner_suburb_town}
                onChange={(value) => fireChange('owner_suburb_town', value)}
                onBlur={handleBlur}
                options={suburbs}
                onValueChange={handleSuburbValueChange}
                placeholder="Owner Suburb or Town"
              />
            </div>
            {touched?.owner_suburb_town && errors.owner_suburb_town && (
              <div className="mui-like-helper-text">{errors.owner_suburb_town}</div>
            )}
          </FormControl>

          <FormControl fullWidth className="mb-5">
            <div className={`mui-like-field${touched?.owner_postcode && errors.owner_postcode ? ' mui-like-field-error' : ''}`}>
              <span className="mui-like-label">Owner Postcode <RequiredAsterisk /></span>
              <AddressAutocompleteInput
                name="owner_postcode"
                inputRef={ownerPostcode}
                value={values.owner_postcode}
                onChange={(value) => fireChange('owner_postcode', value)}
                onBlur={handleBlur}
                options={postcodes}
                onValueChange={handlePostcodeValueChange}
                placeholder="Owner Postcode"
              />
            </div>
            {touched?.owner_postcode && errors.owner_postcode && (
              <div className="mui-like-helper-text">{errors.owner_postcode}</div>
            )}
          </FormControl>

    </Fragment>
  );

}
